import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosClient from '../../api/axiosClient';
import { Lead, LeadStatus } from '../../types';

interface LeadsState {
  myLeads: Lead[];
  allLeads: Lead[];
  status: 'idle' | 'loading' | 'failed';
  error: string | null;
}

const initialState: LeadsState = {
  myLeads: [],
  allLeads: [],
  status: 'idle',
  error: null,
};

export const fetchMyLeads = createAsyncThunk('leads/fetchMine', async (_, { rejectWithValue }) => {
  try {
    const { data } = await axiosClient.get('/leads/mine');
    return data.leads as Lead[];
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.message || 'Could not load your requests');
  }
});

export const fetchAllLeads = createAsyncThunk(
  'leads/fetchAll',
  async (filters: { status?: string; leadType?: string } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axiosClient.get('/admin/leads', { params: filters });
      return data.leads as Lead[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Could not load leads');
    }
  }
);

export const updateLeadStatus = createAsyncThunk(
  'leads/updateStatus',
  async (
    { id, status, contactBy }: { id: string; status: LeadStatus; contactBy?: string },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await axiosClient.patch(`/admin/leads/${id}/status`, { status, contactBy });
      return data.lead as Lead;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Could not update status');
    }
  }
);

const leadsSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyLeads.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMyLeads.fulfilled, (state, action) => {
        state.status = 'idle';
        state.myLeads = action.payload;
      })
      .addCase(fetchMyLeads.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(fetchAllLeads.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAllLeads.fulfilled, (state, action) => {
        state.status = 'idle';
        state.allLeads = action.payload;
      })
      .addCase(fetchAllLeads.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        const idx = state.allLeads.findIndex((l) => l.id === action.payload.id);
        if (idx !== -1) state.allLeads[idx] = action.payload;
      });
  },
});

export default leadsSlice.reducer;
